<template>
  <section class="ai-visibility-competitors-tab">
    <loading-state v-if="loading" label="Loading competitor data..."/>
    <error-state
        v-else-if="loadError"
        :message="loadError"
        retry-label="Retry competitors"
        @retry="loadCompetitors"
    />
    <empty-state
        v-else-if="isEmpty"
        title="No competitor data yet"
        description="Run this AI Visibility tracker to see which competitors AI recommends."
    />
    <div v-else class="ai-visibility-competitors-tab__content">
      <competitor-metrics :summary="competitorsSummary"/>
      <div class="ai-visibility-competitors-tab__panel">
        <competitors-toolbar
            title="Every competitor AI recommends"
            :subtitle="tableSubtitle"
            :search="search"
            @update:search="handleSearchUpdate"
            @export="handleExport"
        />
        <competitors-table
            :rows="paginatedRows"
            :sort-key="sortKey"
            :sort-direction="sortDirection"
            @sort-change="handleSortChange"
            @select-competitor="openCompetitorSidebar"
        />
      </div>
      <table-pagination
          v-if="pageCount > 1"
          :page="currentPage"
          :page-count="pageCount"
          @input="currentPage = $event"
      />
      <competitor-details-sidebar
          v-if="selectedCompetitor"
          :open="isSidebarOpen"
          :competitor="selectedCompetitor"
          :metrics="competitorMetrics"
          :tracked-domain="detail.domain"
          @close="closeCompetitorSidebar"
          @after-close="clearSelectedCompetitor"
          @view-citations="handleViewCitations"
      />
    </div>
  </section>
</template>

<script>
import {getAiVisibilityCompetitors} from '../../../api'
import LoadingState from '../../ui/states/LoadingState.vue'
import ErrorState from '../../ui/states/ErrorState.vue'
import EmptyState from '../../ui/states/EmptyState.vue'
import CompetitorMetrics from './components/CompetitorMetrics.vue'
import CompetitorsToolbar from './components/CompetitorsToolbar.vue'
import CompetitorsTable from './components/CompetitorsTable.vue'
import CompetitorDetailsSidebar from './components/CompetitorDetailsSidebar.vue'
import TablePagination from '../../ui/table/TablePagination.vue'
import {
  buildCompetitorsSummary,
  normalizeCompetitorRows,
} from './competitorMetrics'
import {
  buildCompetitorsCsv,
  COMPETITORS_CSV_FILENAME,
  downloadCsv,
} from './competitorsCsv'

const PAGE_SIZE = 10

function createEmptyCompetitorsData () {
  return {
    metrics: null,
    competitors: {
      rows: [],
    },
  }
}

export default {
  name: 'AiVisibilityCompetitorsTab',
  components: {
    LoadingState,
    ErrorState,
    EmptyState,
    CompetitorMetrics,
    CompetitorsToolbar,
    CompetitorsTable,
    CompetitorDetailsSidebar,
    TablePagination,
  },
  props: {
    siteId: {
      type: [Number, String],
      required: true,
    },
    trackerId: {
      type: [Number, String],
      required: true,
    },
    detail: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
    filterOptions: {
      type: Object,
      default() {
        return {}
      },
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      loadError: '',
      competitorsData: createEmptyCompetitorsData(),
      search: '',
      sortKey: 'mentionRate',
      sortDirection: 'desc',
      currentPage: 1,
      isSidebarOpen: false,
      selectedCompetitorDomain: '',
    }
  },
  computed: {
    competitorRows() {
      return this.competitorsData.competitors.rows
    },
    competitorMetrics() {
      return this.competitorsData.metrics
    },
    normalizedRows() {
      return normalizeCompetitorRows(this.competitorRows, this.competitorMetrics)
    },
    tableSubtitle() {
      return `Sorted by ${this.sortLabel} · ${this.normalizedRows.length} competitors total`
    },
    filteredRows() {
      const query = this.search.trim().toLowerCase()

      if (!query) {
        return this.normalizedRows
      }

      return this.normalizedRows.filter(row => {
        const brandName = (row.brandName || '').toLowerCase()
        const displayName = (row.displayName || '').toLowerCase()
        const domain = (row.domain || '').toLowerCase()

        return brandName.indexOf(query) > -1 ||
            displayName.indexOf(query) > -1 ||
            domain.indexOf(query) > -1
      })
    },
    tableRows() {
      return this.sortRows(this.filteredRows)
    },
    sortLabel() {
      const labels = {
        mentionRate: this.sortDirection === 'desc' ? 'mention rate, highest first' : 'mention rate, lowest first',
        avgRank: this.sortDirection === 'asc' ? 'average rank, best first' : 'average rank, worst first',
        headToHead: this.sortDirection === 'desc' ? 'head-to-head, strongest competitor lead first' : 'head-to-head, strongest your-site lead first',
      }

      return labels[this.sortKey] || 'competitor metrics'
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.tableRows.length / PAGE_SIZE))
    },
    paginatedRows() {
      const startIndex = (this.currentPage - 1) * PAGE_SIZE
      return this.tableRows.slice(startIndex, startIndex + PAGE_SIZE)
    },
    competitorsSummary() {
      return buildCompetitorsSummary(this.competitorMetrics, this.normalizedRows)
    },
    selectedCompetitor() {
      if (!this.selectedCompetitorDomain) {
        return null
      }

      return this.normalizedRows.find(row => row.domain === this.selectedCompetitorDomain) || null
    },
    isEmpty() {
      return !this.competitorsData.metrics || !this.competitorRows.length
    },
  },
  watch: {
    siteId: 'loadCompetitors',
    trackerId: 'loadCompetitors',
    'filters.range': 'loadCompetitors',
    'filters.model': 'loadCompetitors',
    pageCount(pageCount) {
      if (this.currentPage > pageCount) {
        this.currentPage = pageCount
      }
    },
  },
  beforeMount() {
    this.loadCompetitors()
  },
  methods: {
    loadCompetitors() {
      this.loading = true
      this.loadError = ''
      this.currentPage = 1
      this.closeCompetitorSidebar()

      return getAiVisibilityCompetitors(this.siteId, this.trackerId, this.filters)
          .then((response) => {
            this.competitorsData = response.data || createEmptyCompetitorsData()
            this.currentPage = 1
          })
          .catch(() => {
            this.competitorsData = createEmptyCompetitorsData()
            this.loadError = 'Could not load AI Visibility competitor data.'
          })
          .finally(() => {
            this.loading = false
          })
    },
    handleSearchUpdate(value) {
      this.search = value
      this.currentPage = 1
    },
    handleSortChange({key, direction}) {
      this.sortKey = key
      this.sortDirection = direction
      this.currentPage = 1
    },
    handleExport() {
      downloadCsv(
          buildCompetitorsCsv(this.normalizedRows),
          COMPETITORS_CSV_FILENAME
      )
    },
    openCompetitorSidebar(row) {
      if (!row || !row.domain) {
        return
      }

      this.selectedCompetitorDomain = row.domain
      this.$nextTick(() => {
        this.isSidebarOpen = true
      })
    },
    closeCompetitorSidebar() {
      this.isSidebarOpen = false
    },
    clearSelectedCompetitor() {
      this.selectedCompetitorDomain = ''
    },
    handleViewCitations() {
      this.closeCompetitorSidebar()
      if (this.embedded) {
        this.$emit('navigate-tab', {name: 'AiVisibilityCitations'})
        return
      }
      this.$router.push({
        name: 'AiVisibilityCitations',
        params: {
          siteId: String(this.siteId),
          trackerId: String(this.trackerId),
        },
      })
    },
    sortRows(rows) {
      const sortedRows = rows.slice()
      const directionFactor = this.sortDirection === 'asc' ? 1 : -1

      sortedRows.sort((first, second) => {
        const firstValue = this.getSortValue(first, this.sortKey)
        const secondValue = this.getSortValue(second, this.sortKey)

        if (firstValue === secondValue) {
          if (first.isWatched !== second.isWatched) {
            return Number(second.isWatched) - Number(first.isWatched)
          }
          return second.mentionRateRatio - first.mentionRateRatio
        }

        return firstValue > secondValue ? directionFactor : -directionFactor
      })

      return sortedRows
    },
    getSortValue(row, key) {
      if (key === 'mentionRate') {
        return row.mentionRateRatio
      }
      if (key === 'avgRank') {
        return row.averagePosition === null || row.averagePosition === undefined
          ? this.getMissingAveragePositionSortValue()
          : Number(row.averagePosition)
      }
      if (key === 'headToHead') {
        return Number(row.theyBeatCount || 0) - Number(row.youBeatCount || 0)
      }
      return 0
    },
    getMissingAveragePositionSortValue() {
      return this.sortDirection === 'asc'
        ? Number.POSITIVE_INFINITY
        : Number.NEGATIVE_INFINITY
    },
  },
}
</script>

<style lang="scss" scoped>

.ai-visibility-competitors-tab {
  margin-top: 24px;
  padding-bottom: 30px;
}

.ai-visibility-competitors-tab__content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.ai-visibility-competitors-tab__panel {
  overflow: hidden;
  border: 1px solid var(--v-gray12Color-base);
  border-radius: 8px;
  background: var(--v-cardBg-base);
}

@media (max-width: 640px) {
  .ai-visibility-competitors-tab {
    margin-top: 18px;
  }

  .ai-visibility-competitors-tab__content {
    gap: 20px;
  }
}
</style>
